import { useMemo } from 'react';
import { useBlueberryStageReport } from '../../../../services/data/reports/hooks/useBlueberryStageReport';
import { useGetBlocksByVariety } from '../../../../services/entities/hooks/useGetBlocksByVariety';
import { useStages } from '../../../../services/entities/hooks/useStages';
import { StrictDateRange } from '../../../commonUI/DateRangeFilterSelect/types';
import {
  TableColumnDefinitions,
  useLoadTable,
} from '../../../duckDB/hooks/useLoadTable';
import { formatStringList } from '../../../duckDB/utils/formatStringList';

/**
 * Fetch stage report from Wrangler and load the data into duckdb
 * @param customerId
 * @param varietyId
 * @param dateRange
 * @returns loading: whether table is loading
 */
export const useLoadVarietyStageReport = (
  customerId: string | undefined,
  varietyId: string | undefined,
  dateRange: StrictDateRange | null
): boolean => {
  const { blocks, isBlocksLoading, isBlocksFetching } = useGetBlocksByVariety(
    customerId,
    varietyId
  );

  const blockIds = useMemo(() => {
    return formatStringList(blocks?.map((block) => block.id) || []);
  }, [blocks]);

  const { stages, isStagesLoading } = useStages(varietyId);

  const {
    data: blueberryStageReportData,
    isLoading: isBlueberryStageReportLoading,
  } = useBlueberryStageReport(
    customerId,
    stages,
    dateRange ?? undefined,
    isStagesLoading
  );

  const data = useMemo(
    () =>
      blueberryStageReportData?.filter((item) =>
        blockIds.includes(item.block_id)
      ) || [],
    [blueberryStageReportData, blockIds]
  );
  const { isTableLoading } = useLoadTable('stage_report_table', data, {
    columns: STAGE_REPORT_COLUMN_DEFS,
  });

  return (
    isTableLoading ||
    isBlueberryStageReportLoading ||
    isBlocksLoading ||
    isBlocksFetching
  );
};

const STAGE_REPORT_COLUMN_DEFS: TableColumnDefinitions = {
  block_id: 'VARCHAR',
  block_name: 'VARCHAR',
  ranch_id: 'VARCHAR',
  scan_date: 'TIMESTAMP_S',
  scan_id: 'VARCHAR',
  stage_count: 'VARCHAR',
  count_per_plant: 'VARCHAR',
  stage_id: 'VARCHAR',
};
